import React, { useContext, useMemo, useRef, useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import * as R from 'ramda';
import { useNavigate } from 'react-router-dom';
import AddRounded from '@mui/icons-material/AddRounded';
import { func } from 'prop-types';
import { useDispatch } from 'react-redux';

// Local utilities
import { hasAllRoles, hasAnyAndAllRoles, userRoles } from 'utils/currentUserRoles';
import { DRAWER_TYPES } from 'utils/constants';

// Context
import { AuthContext } from 'authentication/AuthContext';
import { DrawerContext } from 'common/drawer/DrawerContext';

// The wizards
import Wizard from 'common/Wizard';
import CreatePtoWizard from 'feature/inventory-management/product-transfer-order/create-wizard/CreateWizard.connector';
import CreateWizard from 'feature/loading-management/carrier-authorizations/create-new-steps/CreateWizard';
import OneToOneReconsignment from 'feature/inventory-management/reconsignments/reconsignmentsDrawer/OneToOneDrawer';

// create PTO
import { resetCreatePto } from 'feature/inventory-management/product-transfer-order/store/createOrder';

// self auth steps imports
import newSelfAuthSteps from 'feature/loading-management/self-authorizations/create-new-steps/Steps';
import {
  openCreateSelfAuthorizationCloseDialog,
  resetWizardData,
} from 'feature/loading-management/self-authorizations/store/createNewSelfAuthorizationSlice';

// supplier auth steps imports
import newSupplierAuthSteps from 'feature/loading-management/supplier-authorizations/create-new-steps/Steps';
import {
  confirmCloseDialog,
  resetSupplierAuthWizardData,
} from 'feature/loading-management/supplier-authorizations/store/createNewSupplierAuthSlice';
import { setDialogDetails } from 'feature/loading-management/supplier-authorizations/store/supplierAuthorizationSlice';
import { openConfirmationDialog } from 'store/Confirmationdialogslice';

// carrier auth
import {
  resetCarrierAuthWizardData,
  setForceClose,
} from 'feature/loading-management/carrier-authorizations/store/createNewCarrierAuthSlice';
import allCarrierAuthSteps from 'feature/loading-management/carrier-authorizations/create-new-steps/Steps';
import { useUserRolePermissions } from 'hooks';

import { filterEnabledFeatures } from 'common/FeatureFlags/Flag';

const createMenuItemClickHandler = (onClick, item) => () => {
  if (item?.onClick?.apply) item.onClick(item.value || item.label);
  if (onClick?.apply) onClick();
};

/**
 * Creates an MuiMenuItem component from a `MenuItem`
 * @param {function} onClick Callback to be executed in MuiMenuItem's onClick
 * @param {MenuItem} item Item object to be transformed into MuiMenuItem component
 * @returns {JSX.Element} MuiMenuItem component
 */
// buildMenuItem :: Function -> Object -> MuiMenuItem
const buildMenuItem = R.curry((onClick, item, index) => (
  <MenuItem
    aria-describedby={item.label}
    className="heap-button"
    disabled={item.disabled}
    key={`${item.group || ''}-${item.label}-${index}`} // Generate unique keys
    onClick={createMenuItemClickHandler(onClick, item)}
  >
    {item.element || item.label}
  </MenuItem>
));

const AddMenu = ({ setIsNavHidden }) => {
  const dispatch = useDispatch();
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const navigate = useNavigate();
  const menuAnchorRef = useRef(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { currentAffiliate } = useUserRolePermissions();

  // roles to be used throughout component
  const { affiliates, affiliateRoles, roles: inputRoles } = useContext(AuthContext);

  const roles = useMemo(
    () => (affiliates.length ? [...inputRoles, ...affiliateRoles] : []),
    [affiliates.length, inputRoles, affiliateRoles]
  );

  const toggleIsMenuOpen = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const { setDrawerComponentProps } = useContext(DrawerContext);

  const addItems = [
    {
      element: 'New 1 to 1 Reconsignment',
      group: 'Inventory',
      label: 'New 1 to 1 Reconsignment',
      onClick: () =>
        setDrawerComponentProps({
          Component: props => <OneToOneReconsignment {...props} />,
          type: DRAWER_TYPES.createReconsignment,
        }),
      rolesAllowed: hasAllRoles([userRoles.PRODUCT_OWNERSHIP_MANAGE, userRoles.INVENTORY_OWNER]),
      value: 'create',
    },
    // We are hiding these option if width is less than 1024px
    ...(lgUp
      ? [
          {
            element: 'New Reconsignments',
            group: 'Inventory',
            label: 'New Reconsignments',
            onClick: () => {
              // Hide nav if there are no unsaved changes
              if (!window.__authSeqHasUnsavedChanges) {
                setIsNavHidden(true);
              }
              navigate(
                `/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}/inventory-management/reconsignments/create/reconsignments`
              );
            },
            rolesAllowed: hasAllRoles([
              userRoles.PRODUCT_OWNERSHIP_MANAGE,
              userRoles.INVENTORY_OWNER,
            ]),
            value: 'create',
          },
        ]
      : []),
    {
      element: 'New Product Transfer Order',
      group: 'Inventory',
      label: 'New Product Transfer Order',
      onClick: () =>
        setDrawerComponentProps({
          Component: props => <CreatePtoWizard isCopyToNew={false} {...props} />,
          resetWizard: resetCreatePto,
          type: DRAWER_TYPES.createNewPto,
        }),
      rolesAllowed: hasAllRoles([userRoles.PRODUCT_OWNERSHIP_MANAGE, userRoles.INVENTORY_OWNER]),
      value: 'create',
    },
    {
      element: 'New Batch',
      group: 'Shipping',

      label: 'New Batch',
      onClick: () => {
        navigate(
          `/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}/shipping/batches/new`
        );
      },
      rolesAllowed: hasAllRoles([userRoles.PRODUCT_SHIPPING_MANAGE]),
      value: 'create',
    },
    {
      element: 'New Monthly Nomination',
      group: 'Shipping',
      label: 'New Monthly Nomination',
      onClick: () => {
        navigate(
          `/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}/shipping/monthly-nominations/new`
        );
      },
      rolesAllowed: hasAllRoles([userRoles.PRODUCT_SHIPPING_MANAGE]),
      value: 'create',
    },
    {
      element: 'New Customer Authorization',
      group: 'Loading',
      label: 'New Customer Authorization',
      onClick: () => {
        // Hide nav if there are no unsaved changes
        if (!window.__authSeqHasUnsavedChanges) {
          setIsNavHidden(true);
        }
        navigate(
          `/affiliate/${currentAffiliate.customerIdName}/${currentAffiliate.dataCompanyId}/loading-management/customer-authorizations/create/customer-information`
        );
      },
      rolesAllowed: hasAllRoles([userRoles.PRODUCT_LOADING_MANAGE, userRoles.SUPPLIER]),
      value: 'create',
    },
    {
      element: 'New Carrier Authorizations',
      group: 'Loading',
      label: 'New Carrier Authorizations',
      onClick: () =>
        setDrawerComponentProps({
          Component: props => (
            <CreateWizard
              steps={allCarrierAuthSteps}
              title="New Carrier Authorizations"
              {...props}
            />
          ),
          drawerPaperSx: { overflowY: 'hidden', width: { lg: 760, xs: 1 } },
          forceCloseSelector: state => state.loadingManagement.createNewCarrierAuth.forceClose,
          holdOpenSelector: state => {
            const { hasUnsavedChanges, submittingBatch } =
              state.loadingManagement.createNewCarrierAuth;
            return hasUnsavedChanges || submittingBatch;
          },
          resetWizard: resetCarrierAuthWizardData,
          setCurrentDialogOpen: () => (dialogDispatch, getState) => {
            const { hasUnsavedChanges, submittingBatch } =
              getState().loadingManagement.createNewCarrierAuth;

            if (hasUnsavedChanges && !submittingBatch) {
              dialogDispatch(
                openConfirmationDialog({
                  cancelAction: () => {},
                  confirmAction: () => dialogDispatch(setForceClose(true)),
                  header: 'Unsaved Changes',
                  leftButtonActionType: 'confirm',
                  leftButtonText: 'Continue',
                  message:
                    'Are you sure you want to leave this page? You have unsaved Carrier Authorizations.',
                  params: {},
                  rightButtonActionType: 'cancel',
                  rightButtonText: 'Keep Editing',
                })
              );
              return;
            }

            dialogDispatch(
              setDialogDetails({
                header: 'Are you sure you want to close the wizard?',
                message:
                  'By closing the wizard you will no longer be able to view the progress of each activation however, all requests will continue until complete. Please confirm you want to close the wizard.',
              })
            );
          },
          type: DRAWER_TYPES.createNewCarrierAuth,
        }),
      rolesAllowed: hasAllRoles([userRoles.PRODUCT_LOADING_MANAGE, userRoles.SUPPLIER]),
      value: 'create',
    },
    {
      element: 'New Self Authorizations',
      group: 'Loading',
      label: 'New Self Authorizations',
      onClick: () =>
        setDrawerComponentProps({
          Component: props => (
            <Wizard steps={newSelfAuthSteps} title="New Self Authorizations" {...props} />
          ),
          forceCloseSelector: state => state.loadingManagement.createNewSelfAuth.forceClose,
          holdOpenSelector: state => {
            const { hasUnsavedChanges, submittingBatch } =
              state.loadingManagement.createNewSelfAuth;
            return hasUnsavedChanges || submittingBatch;
          },
          resetWizard: resetWizardData,
          setCurrentDialogOpen: openCreateSelfAuthorizationCloseDialog,
          type: DRAWER_TYPES.createNewSelfAuth,
        }),
      rolesAllowed: hasAnyAndAllRoles({
        all: [userRoles.PRODUCT_LOADING_MANAGE],
        any: [userRoles.INVENTORY_OWNER, userRoles.SUPPLIER],
      }),
      value: 'create',
    },
    {
      element: 'New Supplier Authorizations',
      group: 'Loading',
      label: 'New Supplier Authorizations',
      onClick: () =>
        setDrawerComponentProps({
          Component: props => (
            <Wizard
              stepHeaderSx={{ pb: 1 }}
              steps={newSupplierAuthSteps}
              title="New Supplier Authorizations"
              {...props}
            />
          ),
          forceCloseSelector: state => state.loadingManagement.createNewSupplierAuth.forceClose,
          holdOpenSelector: state =>
            state.loadingManagement.createNewSupplierAuth.hasUnsavedChanges ||
            state.loadingManagement.createNewSupplierAuth.submittingBatch,
          resetWizard: resetSupplierAuthWizardData,
          setCurrentDialogOpen: () =>
            openConfirmationDialog({
              confirmAction: () => dispatch(confirmCloseDialog()),
              header: 'Unsaved Changes',
              leftButtonActionType: 'confirm',
              leftButtonText: 'Continue',
              message:
                'Are you sure you want to leave this page? You have unsaved Supplier Authorization changes.',
              params: {},
              rightButtonActionType: 'cancel',
              rightButtonText: 'Keep Editing',
            }),
          type: DRAWER_TYPES.createNewSupplierAuth,
        }),
      rolesAllowed: hasAnyAndAllRoles({
        all: [userRoles.PRODUCT_LOADING_MANAGE],
        any: [userRoles.INVENTORY_OWNER, userRoles.SUPPLIER],
      }),
      value: 'create',
    },
  ];

  const groupedItems = R.groupBy(
    item => item.group,
    R.filter(({ rolesAllowed }) => rolesAllowed(roles), filterEnabledFeatures(addItems))
  );

  return (
    !!Object.keys(groupedItems).length && (
      <>
        {lgUp && <Divider flexItem orientation="vertical" />}
        <IconButton
          className="heap-button"
          label="Open drawer"
          onClick={toggleIsMenuOpen}
          size="medium"
          sx={{
            '&:hover': {
              backgroundColor: { lg: 'secondary.light' },
            },
            borderRadius: 0,
            color: 'surface.white',
            height: { lg: 48 },
            ml: { lg: 0, xs: '10px' },
            p: 0,
            width: { lg: 60 },
          }}
        >
          <AddRounded
            className="heap-button"
            data-testid="addIcon"
            ref={menuAnchorRef}
            sx={{
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'surface.white',
              },
              backgroundColor: { lg: 'primary.main', xs: 'surface.white' },
              borderRadius: '100%',
              color: { lg: 'surface.white', xs: 'primary.main' },
              fontSize: 32,
            }}
          />
          <Menu
            anchorEl={menuAnchorRef?.current}
            anchorOrigin={{
              horizontal: lgUp ? 'center' : 28,
              vertical: lgUp ? 36 : 20,
            }}
            disableScrollLock
            onClose={closeMenu}
            open={isMenuOpen}
          >
            {Object.entries(groupedItems).map(([group, items]) => (
              <Box key={group}>
                <ListSubheader sx={{ color: 'primary.dark', fontWeight: 'bold' }}>
                  {R.toUpper(group)}
                </ListSubheader>
                {items.map((item, index) => buildMenuItem(closeMenu, item, index))}
              </Box>
            ))}
          </Menu>
        </IconButton>
      </>
    )
  );
};

AddMenu.propTypes = {
  setIsNavHidden: func.isRequired,
};

export default AddMenu;
